import React, {useEffect, useState} from "react";
import {DialogActions, DialogContent, DialogTitle, Divider, Fade} from "@mui/material";
import Dialog from "../../../base/dialog";
import AddProductTechnicalFileDropZone from "../../../app-specific/dropzone/add-product-technical-file";
import {Col} from "reactstrap";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {readFile, ReadingFileAs} from "../../../../../core/services/utils/utils";
import {InputFileAcceptTypes, ProductTechnicalFileTypes} from "../../../../../core/constants/enums";
import Api from "../../../../../core/services/api/api";
import useAccessLevels from "../../../../hooks/use-access-levels";
import Button from "../../../base/button/button";

const UploadNewProductTechnicalFileVersionDialog = ({open, close, technicalFile, onSuccess}) => {
    const [submitting, setSubmitting] = useState(false);
    const [file, setFile] = useState();
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canEditTechnicalFiles = packages.lookup.editProductTechnicalFiles;

    /**
     * With each change in the open state of the dialog:
     * - if closed, sets the file object to null to clear the file of the dropzone
     */
    useEffect(() => {
        if (!open) {
            setFile(undefined);
        }
    }, [open])

    /**
     * Uploads a new version for the curren technical file document.
     * @param {any} file
     * @return {Promise<undefined>}
     */
    const uploadNewTechnicalFileVersion = async (file) => {
        if (!canEditTechnicalFiles) return;
        setSubmitting(true);
        const forApi = {
            id: technicalFile.id,
            name: technicalFile.name,
            extention: file.extension,
            contentBytes: file.contentBytes,
        }
        const response = await Api.uploadOperatorPartTechnicalFileVersion(forApi);
        if (!isMounted()) return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onSuccess(response.data);
            close();
        }
    }

    /**
     * Sets the selected file in the local state so that it can be used upon form submission.
     * @param files {File[] | null}
     */
    const onFileSelected = async (files) => {
        const selected = files[0];
        const file = {
            name: selected.name,
            extension: selected.name?.split('.')[selected.name?.split('.').length - 1],
            contentBytes: btoa(await readFile(selected, ReadingFileAs.binaryString)),
            url: await readFile(selected, ReadingFileAs.dataUrl),
        }
        let type;
        switch (`.${file.extension}`) {
            case InputFileAcceptTypes.png:
            case InputFileAcceptTypes.jpg:
            case InputFileAcceptTypes.jpeg:
                type = ProductTechnicalFileTypes.IMAGE;
                break;
            case InputFileAcceptTypes.pdf:
                type = ProductTechnicalFileTypes.PDF;
                break;
            case InputFileAcceptTypes.html:
                type = ProductTechnicalFileTypes.HTML;
                break;
            default:
                type = ProductTechnicalFileTypes.unknown;
                break;
        }
        file['type'] = type;
        setFile(file);
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => close()}
                maxWidth={'md'}
                className={'bound-dialog upload-new-technical-file-version-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Upload a new Version
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-3 py-2 d-flex flex-wrap'}>
                    <Col xs={12}>
                        <AddProductTechnicalFileDropZone
                            disabled={submitting}
                            uploading={submitting}
                            onSuccess={onFileSelected}
                            file={file}
                        />
                    </Col>
                </DialogContent>
                <DialogActions className={'d-flex align-items-center justify-content-end'}>
                    <Fade in={!!file}>
                        <Button
                            disabled={!open || submitting}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => uploadNewTechnicalFileVersion(file)}
                            borderRadius={'8px 0 8px 0'}
                        >
                            {submitting ? 'Uploading...' : "Upload"}
                        </Button>
                    </Fade>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default UploadNewProductTechnicalFileVersionDialog;
